import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Drawer, IconButton, InputAdornment, MenuItem, Pagination, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  MdAccountBalance, MdAccountBalanceWallet, MdClose, MdHistory, MdPayments, MdReceiptLong, MdRefresh, MdSearch, MdStorefront, MdTrendingUp, MdWarning,
} from 'react-icons/md';
import { FiFilter } from 'react-icons/fi';
import { getData, postData, putData } from '../../Webservice/Webservice';
import SELLER_PAYOUT_ROUTES from '../../API_Route/SellerPayoutRoute';

const FONT = 'Mulish, sans-serif';
const LIMIT = 15;

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const dt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' }, '&.Mui-disabled': { background: '#f1f5f9', color: '#94a3b8', boxShadow: 'none' } };
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };
const iconBtn = (c) => ({ color: c, background: `${c}14`, borderRadius: '8px', '&:hover': { background: `${c}26` } });

const STATUS = {
  completed: { label: 'Completed', bg: '#dcfce7', fg: '#166534' },
  processing: { label: 'Processing', bg: '#dbeafe', fg: '#1e40af' },
  initiated: { label: 'Initiated', bg: '#fef3c7', fg: '#92400e' },
  failed: { label: 'Failed', bg: '#fee2e2', fg: '#991b1b' },
  pending: { label: 'Pending', bg: '#fef3c7', fg: '#92400e' },
  settled: { label: 'Settled', bg: '#dcfce7', fg: '#166534' },
};
const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.pending;
  return <Chip size="small" label={s.label} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: s.bg, color: s.fg }} />;
};

const Kpi = ({ title, value, hint, icon: Icon, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 200, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, display: 'flex', alignItems: 'center', gap: 1.5, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '12px', p: 1.1, display: 'flex' }}><Icon size={22} /></Box>
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 21, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
      {hint && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, opacity: 0.85 }}>{hint}</Typography>}
    </Box>
  </Box>
);

const DialogHeader = ({ title, sub, accent, onClose, disabled }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, borderBottom: `3px solid ${accent}`, background: '#fff' }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 19 }}>{title}</Typography>
      {sub && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{sub}</Typography>}
    </Box>
    <IconButton onClick={onClose} disabled={disabled} size="small"><MdClose size={20} /></IconButton>
  </Box>
);
const dialogPaper = { sx: { borderRadius: '18px', background: '#f8fafc' } };

const Line = ({ label, value, strong }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.9, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: strong ? 800 : 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);

const MORE = { minPending: '', maxPending: '', minSale: '', maxSale: '' };

function SellerPayout() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ sellers: 0, totalPending: 0, sellersWithPending: 0, totalSales: 0, totalCommission: 0, totalPaid: 0, inFlight: 0, missingBank: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pending, setPending] = useState('');
  const [bank, setBank] = useState('');
  const [more, setMore] = useState(MORE);
  const [drawer, setDrawer] = useState(false);
  const [draft, setDraft] = useState(MORE);
  const seq = useRef(0);

  // popups
  const [seller, setSeller] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [mode, setMode] = useState('manual');
  const [payForm, setPayForm] = useState({ paymentMethod: 'bank_transfer', referenceNumber: '', remarks: '' });
  const [payError, setPayError] = useState('');
  const [paying, setPaying] = useState(false);

  const [bankOpen, setBankOpen] = useState(false);
  const [bankForm, setBankForm] = useState({ accountHolder: '', accountNumber: '', ifsc: '', bankName: '', upiId: '' });
  const [bankErrors, setBankErrors] = useState({});
  const [bankError, setBankError] = useState('');
  const [bankSaving, setBankSaving] = useState(false);

  const [histOpen, setHistOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [checking, setChecking] = useState(null);

  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, pending, bank, ...more };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData(SELLER_PAYOUT_ROUTES.GET_SELLER_PAYOUT, params, authToken(), false);
      if (mine !== seq.current) return;
      setRows(res?.sellers || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setError('Could not load seller payouts.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, pending, bank, more]);
  useEffect(() => { load(); }, [load]);

  const activeMore = Object.values(more).filter(Boolean).length;
  const dirty = !!search || !!pending || !!bank || activeMore > 0;

  // ---------- pay ----------
  const openPay = (s) => {
    setSeller(s);
    setMode(s.bankDetails?.linked ? 'cashfree' : 'manual');
    setPayForm({ paymentMethod: 'bank_transfer', referenceNumber: '', remarks: '' });
    setPayError('');
    setPayOpen(true);
  };
  const submitPay = async () => {
    setPayError('');
    if (mode === 'manual' && !payForm.referenceNumber.trim()) return setPayError('Enter the reference or transaction number of the payment you made.');
    setPaying(true);
    try {
      const res = await postData(SELLER_PAYOUT_ROUTES.UPDATE_SELLER_PAYOUT, {
        sellerId: seller.sellerId,
        paymentMethod: payForm.paymentMethod,
        referenceNumber: payForm.referenceNumber,
        remarks: payForm.remarks,
        useCashfree: mode === 'cashfree',
      }, authToken(), false);
      setPayOpen(false);
      setNotice(res?.message || 'Payout recorded.');
      load();
    } catch (err) {
      setPayError(err?.message || 'The payout could not be completed.');
    } finally {
      setPaying(false);
    }
  };

  // ---------- bank ----------
  const openBank = (s) => {
    setSeller(s);
    setBankForm({ accountHolder: s.bankDetails?.accountHolder || '', accountNumber: '', ifsc: s.bankDetails?.ifsc || '', bankName: s.bankDetails?.bankName || '', upiId: s.bankDetails?.upiId || '' });
    setBankErrors({});
    setBankError('');
    setBankOpen(true);
  };
  const saveBank = async () => {
    setBankError('');
    const e = {};
    if (!bankForm.accountHolder.trim()) e.accountHolder = 'Required';
    const acct = bankForm.accountNumber.replace(/[\s-]/g, '');
    if (!seller.bankDetails?.linked && !acct) e.accountNumber = 'Required';
    else if (acct && !/^\d{6,24}$/.test(acct)) e.accountNumber = '6 to 24 digits';
    if (!/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(bankForm.ifsc.trim())) e.ifsc = 'Like HDFC0001234';
    if (bankForm.upiId && !/^[\w.-]{2,}@[a-zA-Z]{3,}$/.test(bankForm.upiId.trim())) e.upiId = 'Like name@bank';
    setBankErrors(e);
    if (Object.keys(e).length) return;
    setBankSaving(true);
    try {
      await putData(`${SELLER_PAYOUT_ROUTES.UPDATE_BANK_DETAILS}/${seller.sellerId}`, bankForm, authToken(), true);
      setBankOpen(false);
      setNotice('Bank details saved.');
      load();
    } catch (err) {
      setBankError(err?.message || 'Could not save the bank details.');
    } finally {
      setBankSaving(false);
    }
  };

  // ---------- history ----------
  const loadHistory = async (s) => {
    setHistLoading(true);
    try {
      const res = await getData(`${SELLER_PAYOUT_ROUTES.GET_SELLER_PAYOUT_HISTORY}/${s.sellerId}`, undefined, authToken(), false);
      setHistory(res?.payouts || []);
    } catch { setHistory([]); } finally { setHistLoading(false); }
  };
  const openHistory = (s) => { setSeller(s); setHistOpen(true); setHistory([]); loadHistory(s); };
  const checkStatus = async (id) => {
    setChecking(id);
    try {
      await getData(`${SELLER_PAYOUT_ROUTES.CHECK_PAYOUT_STATUS}/${id}`, undefined, authToken(), false);
      await loadHistory(seller);
      load();
    } catch { /* the service shows the error */ } finally { setChecking(null); }
  };

  // ---------- ledger ----------
  const openLedger = async (s) => {
    setSeller(s);
    setLedgerOpen(true);
    setLedger([]);
    setLedgerLoading(true);
    try {
      const res = await getData(`${SELLER_PAYOUT_ROUTES.GET_SELLER_LEDGER}/${s.sellerId}`, { status: 'pending' }, authToken(), false);
      setLedger(res?.ledgers || []);
    } catch { setLedger([]); } finally { setLedgerLoading(false); }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Seller payouts</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>What each seller has earned, what you have paid, and what is still owed.</Typography>
        </Box>
        <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}
      {stats.inFlight > 0 && <Alert severity="info" sx={{ mb: 2 }}>{stats.inFlight} gateway transfer(s) are still processing. Open the seller’s history and press Check to update them.</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="Owed to sellers" value={inr(stats.totalPending)} hint={`${stats.sellersWithPending} seller(s) awaiting`} icon={MdAccountBalanceWallet} from="#f59e0b" to="#b45309" active={pending === 'yes'} onClick={() => { setPending(pending === 'yes' ? '' : 'yes'); setPage(1); }} />
        <Kpi title="Paid so far" value={inr(stats.totalPaid)} icon={MdPayments} from="#22c55e" to="#15803d" />
        <Kpi title="Gross sales" value={inr(stats.totalSales)} hint={`Commission ${inr(stats.totalCommission)}`} icon={MdTrendingUp} from="#6366f1" to="#4338ca" />
        <Kpi title="Sellers" value={stats.sellers} icon={MdStorefront} from="#0ea5e9" to="#0369a1" />
        <Kpi title="Owed but no bank details" value={stats.missingBank} icon={MdWarning} from="#e11d48" to="#9f1239" active={pending === 'yes' && bank === 'missing'} onClick={() => { const on = pending === 'yes' && bank === 'missing'; setPending(on ? '' : 'yes'); setBank(on ? '' : 'missing'); setPage(1); }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search seller, business, email or phone…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Balance" value={pending} onChange={(e) => { setPending(e.target.value); setPage(1); }} sx={{ minWidth: 160 }}>
          <MenuItem value="">All sellers</MenuItem><MenuItem value="yes">Money owed</MenuItem><MenuItem value="no">Nothing owed</MenuItem>
        </TextField>
        <TextField select size="small" label="Bank details" value={bank} onChange={(e) => { setBank(e.target.value); setPage(1); }} sx={{ minWidth: 160 }}>
          <MenuItem value="">Any</MenuItem><MenuItem value="linked">Linked</MenuItem><MenuItem value="missing">Missing</MenuItem>
        </TextField>
        <Button startIcon={<FiFilter />} onClick={() => { setDraft(more); setDrawer(true); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '10px', px: 2 }}>
          Amounts{activeMore ? ` (${activeMore})` : ''}
        </Button>
        {dirty && <Button onClick={() => { setSearch(''); setPending(''); setBank(''); setMore(MORE); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Seller</TableCell>
                <TableCell sx={head} align="right">Sales</TableCell>
                <TableCell sx={head} align="right">Commission</TableCell>
                <TableCell sx={head} align="right">Paid</TableCell>
                <TableCell sx={head} align="right">Owed now</TableCell>
                <TableCell sx={head}>Last payout</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(7)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No sellers match these filters.</TableCell></TableRow>}
              {!loading && rows.map((s) => {
                const owed = s.pendingBalance > 0;
                return (
                  <TableRow key={s.sellerId} hover>
                    <TableCell sx={cell}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar sx={{ width: 34, height: 34, bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 800, fontSize: 14 }}>{(String(s.sellerName || 'S')[0] || 'S').toUpperCase()}</Avatar>
                        <Box>
                          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{s.sellerName || 'Seller'}</Typography>
                          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{s.business_name || s.email || ''}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={cell} align="right">{inr(s.totalSaleAmount)}</TableCell>
                    <TableCell sx={{ ...cell, color: '#dc2626' }} align="right">{inr(s.adminCommissionAmount)}</TableCell>
                    <TableCell sx={{ ...cell, color: '#166534', fontWeight: 700 }} align="right">{inr(s.totalPaidToSeller)}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, color: owed ? '#b45309' : '#94a3b8' }}>{inr(s.pendingBalance)}</Typography>
                      {s.pendingLedgerCount > 0 && (
                        <Typography onClick={() => openLedger(s)} sx={{ fontFamily: FONT, fontSize: 12, color: '#4f46e5', cursor: 'pointer', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>{s.pendingLedgerCount} order{s.pendingLedgerCount === 1 ? '' : 's'}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cell}>
                      {s.lastPayoutDate ? <><div>{fmtDate(s.lastPayoutDate)}</div><StatusBadge status={s.lastPayoutStatus} /></> : <span style={{ color: '#94a3b8' }}>Never</span>}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button size="small" disabled={!owed || s.payoutsInFlight > 0} onClick={() => openPay(s)} sx={{ ...yellowBtn, px: 1.75, py: 0.5, fontSize: 12.5 }}>Pay</Button>{' '}
                      <Tooltip title="Payout history"><IconButton size="small" onClick={() => openHistory(s)} sx={iconBtn('#4f46e5')}><MdHistory size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title={s.bankDetails?.linked ? `Bank details · ${s.bankDetails.accountMasked}` : 'Add bank details'}><IconButton size="small" onClick={() => openBank(s)} sx={iconBtn(s.bankDetails?.linked ? '#16a34a' : '#dc2626')}><MdAccountBalance size={17} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} seller{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* amounts drawer */}
      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 340 }, p: 3 } }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 2 }}>Filter by amount</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[['minPending', 'Owed — at least (₹)'], ['maxPending', 'Owed — at most (₹)'], ['minSale', 'Sales — at least (₹)'], ['maxSale', 'Sales — at most (₹)']].map(([k, l]) => (
            <TextField key={k} size="small" type="number" label={l} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} inputProps={{ min: 0 }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
          <Button fullWidth onClick={() => { setDraft(MORE); setMore(MORE); setPage(1); setDrawer(false); }} sx={{ textTransform: 'none', fontWeight: 700 }}>Reset</Button>
          <Button fullWidth variant="contained" onClick={() => { setMore(draft); setPage(1); setDrawer(false); }} sx={yellowBtn}>Apply</Button>
        </Box>
      </Drawer>

      {/* pay */}
      <Dialog open={payOpen} onClose={() => !paying && setPayOpen(false)} maxWidth="sm" fullWidth scroll="paper" PaperProps={dialogPaper}>
        <DialogHeader title="Pay seller" sub={seller?.sellerName} accent="rgb(255 209 2)" onClose={() => setPayOpen(false)} disabled={paying} />
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ p: 2.25, borderRadius: '14px', background: '#fffbeb', border: '1px solid #fde68a' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: '#92400e' }}>AMOUNT TO PAY</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 30, fontWeight: 800, color: '#78350f', lineHeight: 1.15 }}>{inr(seller?.pendingBalance)}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#a16207' }}>Settles {seller?.pendingLedgerCount || 0} order(s). This cannot be split.</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {[
              { key: 'cashfree', label: 'Pay through gateway', desc: 'Cashfree sends it to the seller’s bank', disabled: !seller?.bankDetails?.linked },
              { key: 'manual', label: 'I already paid', desc: 'Record a payment made outside the system', disabled: false },
            ].map((m) => (
              <Box key={m.key} onClick={() => !m.disabled && setMode(m.key)} sx={{ flex: 1, minWidth: 220, p: 1.75, borderRadius: '12px', cursor: m.disabled ? 'not-allowed' : 'pointer', opacity: m.disabled ? 0.5 : 1, border: '2px solid', borderColor: mode === m.key ? '#f59e0b' : '#e2e8f0', background: mode === m.key ? '#fffbeb' : '#fff' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{m.label}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{m.disabled ? 'Add bank details first' : m.desc}</Typography>
              </Box>
            ))}
          </Box>

          {mode === 'cashfree' && seller?.bankDetails?.linked && (
            <Box sx={{ p: 2, borderRadius: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 800, color: '#166534', mb: 0.5 }}>TRANSFER TO</Typography>
              <Line label="Account holder" value={seller.bankDetails.accountHolder} />
              <Line label="Account" value={seller.bankDetails.accountMasked} />
              <Line label="IFSC" value={seller.bankDetails.ifsc} />
              <Line label="Bank" value={seller.bankDetails.bankName} />
            </Box>
          )}

          {mode === 'manual' && (
            <>
              <TextField select size="small" label="How you paid" value={payForm.paymentMethod} onChange={(e) => setPayForm({ ...payForm, paymentMethod: e.target.value })}>
                <MenuItem value="bank_transfer">Bank transfer</MenuItem><MenuItem value="upi">UPI</MenuItem><MenuItem value="cheque">Cheque</MenuItem><MenuItem value="cash">Cash</MenuItem><MenuItem value="other">Other</MenuItem>
              </TextField>
              <TextField size="small" label="Reference / transaction number *" value={payForm.referenceNumber} onChange={(e) => setPayForm({ ...payForm, referenceNumber: e.target.value })} inputProps={{ maxLength: 100 }} />
            </>
          )}
          <TextField size="small" label="Remarks (optional)" multiline rows={2} value={payForm.remarks} onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })} inputProps={{ maxLength: 500 }} />
          {payError && <Alert severity="error">{payError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setPayOpen(false)} disabled={paying} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={submitPay} variant="contained" disabled={paying} startIcon={paying ? <CircularProgress size={15} color="inherit" /> : <MdPayments />} sx={yellowBtn}>
            {paying ? 'Processing…' : mode === 'cashfree' ? `Send ${inr(seller?.pendingBalance)}` : 'Record payout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* bank */}
      <Dialog open={bankOpen} onClose={() => !bankSaving && setBankOpen(false)} maxWidth="xs" fullWidth PaperProps={dialogPaper}>
        <DialogHeader title="Bank details" sub={seller?.sellerName} accent="#3b82f6" onClose={() => setBankOpen(false)} disabled={bankSaving} />
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField size="small" label="Account holder *" value={bankForm.accountHolder} onChange={(e) => setBankForm({ ...bankForm, accountHolder: e.target.value })} error={!!bankErrors.accountHolder} helperText={bankErrors.accountHolder} />
          <TextField size="small" label={seller?.bankDetails?.linked ? 'Account number' : 'Account number *'} value={bankForm.accountNumber} onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })} error={!!bankErrors.accountNumber}
            helperText={bankErrors.accountNumber || (seller?.bankDetails?.linked ? `On file: ${seller.bankDetails.accountMasked}. Leave blank to keep it.` : '')} inputProps={{ inputMode: 'numeric' }} />
          <TextField size="small" label="IFSC *" value={bankForm.ifsc} onChange={(e) => setBankForm({ ...bankForm, ifsc: e.target.value.toUpperCase() })} error={!!bankErrors.ifsc} helperText={bankErrors.ifsc} inputProps={{ maxLength: 11 }} />
          <TextField size="small" label="Bank name" value={bankForm.bankName} onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })} />
          <TextField size="small" label="UPI ID (optional)" value={bankForm.upiId} onChange={(e) => setBankForm({ ...bankForm, upiId: e.target.value })} error={!!bankErrors.upiId} helperText={bankErrors.upiId} />
          {bankError && <Alert severity="error">{bankError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setBankOpen(false)} disabled={bankSaving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={saveBank} variant="contained" disabled={bankSaving} sx={yellowBtn}>{bankSaving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* history */}
      <Dialog open={histOpen} onClose={() => setHistOpen(false)} maxWidth="md" fullWidth scroll="paper" PaperProps={dialogPaper}>
        <DialogHeader title="Payout history" sub={seller?.sellerName} accent="#8b5cf6" onClose={() => setHistOpen(false)} />
        <DialogContent sx={{ p: 0 }}>
          {histLoading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box> : history.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, color: '#64748b', textAlign: 'center', py: 5 }}>No payouts yet.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={head}>Date</TableCell><TableCell sx={head} align="right">Amount</TableCell><TableCell sx={head}>Method</TableCell><TableCell sx={head}>Reference</TableCell><TableCell sx={head}>By</TableCell><TableCell sx={head}>Status</TableCell><TableCell sx={head} />
                </TableRow></TableHead>
                <TableBody>
                  {history.map((p) => (
                    <TableRow key={p._id} hover>
                      <TableCell sx={cell}>{dt(p.createdAt)}</TableCell>
                      <TableCell sx={{ ...cell, fontWeight: 800 }} align="right">{inr(p.amount)}</TableCell>
                      <TableCell sx={{ ...cell, textTransform: 'capitalize' }}>{p.payoutGateway ? `${p.payoutGateway} · ` : ''}{String(p.paymentMethod || '—').replace(/_/g, ' ')}</TableCell>
                      <TableCell sx={{ ...cell, color: '#64748b', wordBreak: 'break-all' }}>{p.referenceNumber || p.gatewayTransferId || '—'}
                        {p.bankSnapshot?.accountMasked && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>{p.bankSnapshot.accountMasked}</Typography>}
                      </TableCell>
                      <TableCell sx={cell}>{p.initiatedByName || '—'}</TableCell>
                      <TableCell><StatusBadge status={p.status} />{p.remarks && <Tooltip title={p.remarks}><Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8', cursor: 'help' }}>note</Typography></Tooltip>}</TableCell>
                      <TableCell>
                        {p.payoutGateway === 'cashfree' && ['processing', 'initiated'].includes(p.status) && (
                          <Button size="small" onClick={() => checkStatus(p._id)} disabled={checking === p._id} startIcon={checking === p._id ? <CircularProgress size={12} /> : <MdRefresh size={14} />} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}>Check</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setHistOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* pending orders */}
      <Dialog open={ledgerOpen} onClose={() => setLedgerOpen(false)} maxWidth="md" fullWidth scroll="paper" PaperProps={dialogPaper}>
        <DialogHeader title="Orders waiting to be paid" sub={seller?.sellerName} accent="#f59e0b" onClose={() => setLedgerOpen(false)} />
        <DialogContent sx={{ p: 0 }}>
          {ledgerLoading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box> : ledger.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, color: '#64748b', textAlign: 'center', py: 5 }}>No pending orders.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell sx={head}>Order</TableCell><TableCell sx={head}>Date</TableCell><TableCell sx={head}>Type</TableCell><TableCell sx={head} align="right">Gross</TableCell><TableCell sx={head} align="right">Commission</TableCell><TableCell sx={head} align="right">Net</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {ledger.map((l) => (
                    <TableRow key={l._id} hover>
                      <TableCell sx={{ ...cell, fontWeight: 700 }}>{l.orderId?.orderNumber ? `#${l.orderId.orderNumber}` : '—'}</TableCell>
                      <TableCell sx={cell}>{fmtDate(l.orderId?.createdAt || l.createdAt)}</TableCell>
                      <TableCell sx={{ ...cell, textTransform: 'capitalize' }}>{l.type}</TableCell>
                      <TableCell sx={cell} align="right">{inr(l.grossAmount)}</TableCell>
                      <TableCell sx={{ ...cell, color: '#dc2626' }} align="right">{inr(l.commissionAmount)} <span style={{ color: '#94a3b8' }}>({l.commissionPercentage}%)</span></TableCell>
                      <TableCell sx={{ ...cell, fontWeight: 800, color: l.netAmount < 0 ? '#dc2626' : '#166534' }} align="right">{inr(l.netAmount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ ...cell, fontWeight: 800 }}>Total</TableCell>
                    <TableCell align="right" sx={{ ...cell, fontWeight: 800 }}>{inr(ledger.reduce((a, l) => a + (l.netAmount || 0), 0))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setLedgerOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
          {ledger.length > 0 && seller?.payoutsInFlight === 0 && <Button onClick={() => { setLedgerOpen(false); openPay(seller); }} startIcon={<MdReceiptLong />} variant="contained" sx={yellowBtn}>Pay {inr(seller?.pendingBalance)}</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SellerPayout;
